/**
 * Pure boot logic for the Praesto PWA — no React, no Firebase, fully
 * unit-testable. `App.tsx` wires the live CoreAPI observers into these:
 *
 *   parse tenantSlug → createCoreAPI → auth → tenant settings →
 *   loadActiveModules → router/layout
 *
 * (see apps/web/CLAUDE.md "Boot flow").
 */
export function parseTenantSlug(pathname: string): string | null {
  const first = pathname.split("/").filter(Boolean)[0];
  return first ?? null;
}

export interface BootInputs {
  readonly tenantSlug: string | null;
  readonly authResolved: boolean;
  readonly user: { readonly id: string } | null;
  readonly settingsLoaded: boolean;
  readonly modulesLoaded: boolean;
  /**
   * True once memberships + globalRole claims have been pushed into the
   * RBAC singleton. Without this gate, SubNavSidebar (and any other
   * RBAC-filtered UI) renders before `core.rbac.can()` knows about the
   * user's role and shows an empty list. See manifests-context.tsx note.
   */
  readonly rbacReady: boolean;
  readonly error?: string;
}

export type BootState =
  | { readonly phase: "no-tenant" }
  | { readonly phase: "recognizing" }
  | { readonly phase: "no-session" }
  | { readonly phase: "booting" }
  | { readonly phase: "login"; readonly tenantSlug: string }
  | { readonly phase: "ready"; readonly tenantSlug: string }
  | { readonly phase: "error"; readonly message: string };

export function computeBootState(i: BootInputs): BootState {
  if (i.error) return { phase: "error", message: i.error };
  if (!i.tenantSlug) return { phase: "no-tenant" };
  if (!i.authResolved) return { phase: "booting" };
  if (!i.user) return { phase: "login", tenantSlug: i.tenantSlug };
  if (!i.settingsLoaded || !i.modulesLoaded || !i.rbacReady) {
    return { phase: "booting" };
  }
  return { phase: "ready", tenantSlug: i.tenantSlug };
}
